import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { WalkthroughProvider } from "@/contexts/WalkthroughContext";
import { StudentProvider } from "@/contexts/StudentContext";
import { useFirestoreSync } from "@/hooks/useFirestoreSync";
import { useSubscriptionVerification } from "@/hooks/useSubscriptionVerification";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { detectSubdomainContext, SubdomainContext } from "@/lib/subdomainDetection";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { FloatingFeedbackButton } from "@/components/shared/FloatingFeedbackButton";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { useRoleAuth } from "@/hooks/useRoleAuth";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// ── Page loader fallback ──────────────────────────────────────────────────────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// ── Lazy page imports ─────────────────────────────────────────────────────────
const Index = lazy(() => import('./pages/Index'));
const Auth = lazy(() => import('./pages/Auth'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Universities = lazy(() => import('./pages/Universities'));
const UniversityDetail = lazy(() => import('./pages/UniversityDetail'));
const Documents = lazy(() => import('./pages/Documents'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Deadlines = lazy(() => import('./pages/Deadlines'));
const ActivityLogs = lazy(() => import('./pages/ActivityLogs'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const Admin = lazy(() => import('./pages/Admin'));
const ModeratorDashboard = lazy(() => import('./pages/ModeratorDashboard'));
const Community = lazy(() => import('./pages/Community'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Contact = lazy(() => import('./pages/Contact'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
// Consultant pages
const ConsultantDashboard = lazy(() => import('./pages/consultant/ConsultantDashboard'));
const ConsultantOnboarding = lazy(() => import('./pages/consultant/ConsultantOnboarding'));
const ConsultantSuspended = lazy(() => import('./pages/consultant/ConsultantSuspended'));
const AddStudent = lazy(() => import('./pages/consultant/AddStudent'));
const StudentDetail = lazy(() => import('./pages/consultant/StudentDetail'));
const ConsultantStaffPage = lazy(() => import('./pages/consultant/ConsultantStaff'));
const ConsultantStaffPortal = lazy(() => import('./pages/consultant/ConsultantStaffPortal'));
const ConsultantSettings = lazy(() => import('./pages/consultant/ConsultantSettings'));
const ConsultantMessages = lazy(() => import('./pages/consultant/ConsultantMessages'));
const ConsultantAnalytics = lazy(() => import('./pages/consultant/ConsultantAnalytics'));
const StaffSettings = lazy(() => import('./pages/consultant/StaffSettings'));
// Student portal
const StudentPortal = lazy(() => import('./pages/student/StudentPortal'));
const PortalAccessDenied = lazy(() => import('./pages/student/PortalAccessDenied'));
const SubdomainStudentPortal = lazy(() => import('./pages/student/SubdomainStudentPortal'));
// Phase 6 additions
const ConsultantDirectory = lazy(() => import('./pages/ConsultantDirectory'));
const ServerError = lazy(() => import('./pages/ServerError'));
// Phase 8 additions
const DomainNotFound = lazy(() => import('./pages/DomainNotFound'));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

function AppContent() {
  // Sync Firestore data with local state (for student / regular users)
  useFirestoreSync();

  // Verify subscription status from server (authoritative source of truth)
  useSubscriptionVerification();

  // Track page views and user behavior for analytics
  useAnalytics();

  const { user } = useAuth();
  const { isAdmin, isModerator, role, userRole, isChecking } = useRoleAuth();
  const consultantAccountId = isChecking
    ? ''
    : role === 'consultant_staff'
      ? (userRole?.consultantId ?? '')
      : (user?.uid ?? '');

  // Subdomain / custom domain detection
  const [subdomainCtx, setSubdomainCtx] = useState<SubdomainContext | null>(null);
  const [subdomainChecked, setSubdomainChecked] = useState(false);

  useEffect(() => {
    detectSubdomainContext().then(ctx => {
      setSubdomainCtx(ctx);
      setSubdomainChecked(true);
    });
  }, []);

  // Wait for subdomain check before rendering (avoids flash)
  if (!subdomainChecked) return <PageLoader />;

  // Custom domain — override routing entirely
  if (subdomainCtx?.isCustomDomain) {
    if (!subdomainCtx.consultantId)
      return (
        <Suspense fallback={<PageLoader />}>
          <DomainNotFound />
        </Suspense>
      );
    return (
      <WalkthroughProvider>
        <Suspense fallback={<PageLoader />}>
          <SubdomainStudentPortal consultantId={subdomainCtx.consultantId} />
        </Suspense>
      </WalkthroughProvider>
    );
  }

  return (
    <WalkthroughProvider>
      <Suspense fallback={<PageLoader />}>
        <SentryRoutes>
          {/* ── Public / student routes ────────────────────────────────── */}
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/universities" element={<Universities />} />
          <Route path="/universities/:id" element={<UniversityDetail />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/deadlines" element={<Deadlines />} />
          <Route path="/community" element={<Community />} />
          <Route path="/logs" element={<ActivityLogs />} />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />

          {/* ── Admin / Moderator ─────────────────────────────────────── */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <Admin />
              </RequireAuth>
            }
          />
          <Route
            path="/moderator"
            element={
              <RequireAuth>
                <ModeratorDashboard />
              </RequireAuth>
            }
          />

          {/* ── Consultant portal (all routes share StudentProvider) ───── */}
          <Route
            path="/consultant/*"
            element={
              <RequireAuth>
                <StudentProvider consultantId={consultantAccountId}>
                  <SentryRoutes>
                    <Route path="onboarding" element={<ConsultantOnboarding />} />
                    <Route path="suspended" element={<ConsultantSuspended />} />
                    <Route path="dashboard" element={<ConsultantDashboard />} />
                    <Route path="students/new" element={<AddStudent />} />
                    <Route path="students/:studentId" element={<StudentDetail />} />
                    <Route path="staff" element={<ConsultantStaffPage />} />
                    <Route path="staff-portal" element={<ConsultantStaffPortal />} />
                    <Route path="settings" element={<ConsultantSettings />} />
                    <Route path="staff-settings" element={<StaffSettings />} />
                    <Route path="messages" element={<ConsultantMessages />} />
                    <Route path="analytics" element={<ConsultantAnalytics />} />
                    <Route path="*" element={<ConsultantDashboard />} />
                  </SentryRoutes>
                </StudentProvider>
              </RequireAuth>
            }
          />

          {/* ── Student self-service portal ───────────────────────────── */}
          <Route
            path="/student/*"
            element={
              <RequireAuth>
                <SentryRoutes>
                  <Route path="dashboard" element={<StudentPortal />} />
                  <Route path="access-denied" element={<PortalAccessDenied />} />
                  <Route path="*" element={<StudentPortal />} />
                </SentryRoutes>
              </RequireAuth>
            }
          />

          {/* ── Static / marketing pages ──────────────────────────────── */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/about" element={<AboutUs />} />
          {/* ── Phase 6 additions ─────────────────────────────────────── */}
          <Route path="/consultants" element={<ConsultantDirectory />} />
          <Route path="/error" element={<ServerError />} />
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
        {/* Only show feedback button for regular users and consultants (not admin/mod/staff/student) */}
        {!isAdmin && !isModerator && role !== 'consultant_staff' && role !== 'student' && (
          <FloatingFeedbackButton />
        )}
        <PWAInstallPrompt />
      </Suspense>
    </WalkthroughProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <Toaster />
              <Sonner />
              <AppContent />
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
